import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "@shared/shared/tickerFilterInput";
import * as Layout from "@shared/ui/wheelScreenerLayout";
import {
  displayTableColumns,
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  renderTablePinnedAttr,
  type ColumnState,
} from "@shared/ui/tableColumns";
import {
  compactSubline,
  isWebCompactMode,
  resolveWebTableColumns,
} from "@shared/ui/webCompactMode";
import { renderFocusRecRowMenu } from "@shared/ui/focusActions";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  recommendationLedgerMark,
  subscribeLedgerMarks,
} from "@shared/ui/ledgerMarks";
import { scopeAllowsRecommendation, subscribeAccountScope } from "@shared/ui/accountScope";
import { withPreservedInputFocus } from "@shared/ui/preserveInputFocus";
import {
  renderScreenerExpiryInnerHtml,
  screenerExpiryDateLabel,
  screenerExpiryEarningsTip,
} from "@shared/ui/screenerExpiryCell";
import { renderScreenerSharesCellHtml } from "@shared/ui/screenerSharesCell";
import { indexBadgesForTicker, renderIndexBadgesHtml } from "@shared/ui/indexMembership";
import {
  callStrikeVsAvgCost,
  callStrikeVsAvgTip,
  callStrikeVsAvgValueClass,
  earningsDateTip,
  fmtEarningsDate,
  fmtEarningsInDays,
  earningsWithinExpiry,
  fmtNum,
  fmtUsd,
  fmtWheelRatio,
  invalidateWheelSnapshots,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  normalizeTicker,
  peekPositionsSnapshot,
  peekRecommendationsSnapshot,
  resolveShareAvgCostForTicker,
  sharesForTicker,
  type PositionsSnapshot,
  type WheelRecommendation,
} from "@shared/ui/watchlistData";
import { fmtProfitPct, recommendationProfitability } from "@shared/ui/profitability";
import {
  fetchRiskyCallRecommendations,
  isRiskyCallRecommendation,
  mergeCallRecommendations,
} from "@shared/ui/callsPlus";
import {
  applyTickerEarnings,
  bindCallsRiskyPopRangeSlider,
  callAssignmentMaxLoss,
  callCoveredPremiumProfit,
  defaultCallsRiskyPopRange,
  meetsRiskyPopRange,
  parseCallsRiskyPopRange,
  renderCallsRiskyPopRangeSlider,
  riskyCoveredShares,
  type CallsRiskyPopRange,
} from "@shared/ui/callsRiskyStrike";
import { loadOwnLedgerStrikeAvgMap } from "@shared/ui/ownLedgerClient";
import { teardownMultiSelectPortals } from "./multiSelectPortals";
import { paintPanelLoadError } from "./authUi";
import {
  ADVICE_UPDATED_EVENT,
  adviceBadgeHtml,
  adviceRowAttrs,
  adviceSeedToScreenerRow,
  advisoryFilterFieldHtml,
  appendSyntheticAdviceRows,
  bindScreenerAdvice,
  applyAdviceMarks,
  pinAdvisedScreenerRows,
  screenerAdviceButtonHtml,
  screenerContractKey,
  screenerRowIsAdvised,
} from "@shared/ui/agentAdviceOverlay";
import { morphTableCell, reconcileTableBodyByKey, rowFromHtml } from "@shared/ui/tableRowPatch";
import { enrichAdviceScreenerRow, hydrateAdviceScreenerRows } from "@shared/ui/adviceScreenerEnrich";

const CALL_FILTERS_KEY = "sfe-web-call-filters-v1";
const TABLE_ID = "web-calls-screener" as const;

const CALL_DTE_BUCKETS = [
  { key: "1-7", min: 1, max: 7, label: "1–7" },
  { key: "8-15", min: 8, max: 15, label: "8–15" },
  { key: "16-24", min: 16, max: 24, label: "16–24" },
  { key: "25-35", min: 25, max: 35, label: "25–35" },
  { key: "36-45", min: 36, max: 45, label: "36–45" },
  { key: "46-60", min: 46, max: 60, label: "46–60" },
  { key: "61+", min: 61, max: Infinity, label: "61+" },
];

const EXTRA_COLUMN_META: Record<
  string,
  {
    label: string;
    num?: boolean;
    heldTh?: boolean;
    hint?: string;
    compactHint?: string;
    sortKey?: string;
    sortType?: string;
  }
> = {
  next_earnings_date: {
    label: "Earnings",
    sortKey: "next_earnings_date",
    sortType: "str",
    hint: "Next earnings date for the underlying (via Finviz). Subline: days until that date; amber when it lands on or before expiry.",
  },
  sfe_profitability: {
    label: "Profit.",
    num: true,
    hint: "Premium as % of your average share cost: last option price ÷ avg cost.",
    compactHint: "Subline: annualized return on the committed share value (Ann. ROC).",
  },
  sfe_max_loss: {
    label: "Max loss",
    num: true,
    hint: "Locked-in share loss if every held lot is assigned at this strike: (avg cost − strike) × shares. Shares = floor(held ÷ 100) × 100.",
    sortKey: "sfe_max_loss",
    sortType: "num",
  },
  sfe_profit: {
    label: "Profit",
    num: true,
    hint: "Premium if you sell covered calls on all held shares: last (or mid) × shares. Shares = floor(held ÷ 100) × 100.",
    sortKey: "sfe_profit",
    sortType: "num",
  },
  broker_mark: {
    label: "Br",
    sortKey: "broker_mark",
    sortType: "str",
    hint: "Broker. RH is Robinhood, TT Tastytrade, WB Webull, FD Fidelity. Show, hide, or move it in column settings.",
  },
  account_short: {
    label: "Ac",
    sortKey: "account_short",
    sortType: "str",
    hint: "Short account name from Settings. It starts as a shortened account name and you can rename it.",
  },
};

export interface CallFilterState {
  quote: "tradable" | "all";
  dte: string;
  earnings: "before" | "all";
  ticker: string;
  shares: "all" | "available";
  sectors: string[];
  rubricBands: string[];
  caps: string[];
  risky: boolean;
  riskyPopRange: CallsRiskyPopRange;
}

interface CallsPanelCache {
  allRows: WheelRecommendation[];
  positions: PositionsSnapshot;
  stockAvgs: Map<string, number>;
  fetchedAt: number;
  riskyMerged: boolean;
}

let callsPanelCache: CallsPanelCache | null = null;
let callsPanelInflight: Promise<CallsPanelCache> | null = null;
let callsMountedPanel: HTMLElement | null = null;
let callsRerender: (() => void) | null = null;
subscribeAccountScope(() => callsRerender?.());
subscribeLedgerMarks(() => {
  callsRerender?.();
});
const CALLS_CACHE_MS = 60_000;

if (typeof window !== "undefined") {
  window.addEventListener(ADVICE_UPDATED_EVENT, () => {
    if (callsMountedPanel?.isConnected && callsMountedPanel.getAttribute("data-wheel-view") === "calls") {
      callsRerender?.();
    }
  });
}

function callsCacheMissingAvgs(cache: CallsPanelCache | null): boolean {
  return !cache || cache.stockAvgs.size === 0;
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function isTradableQuote(r: WheelRecommendation): boolean {
  const mid = Number(r.mid);
  const last = Number(r.last_price);
  if (!(mid > 0) || !(last > 0)) return false;
  const ref = Math.max(mid, last, 0.01);
  if (Math.abs(mid - last) / ref > 0.15 && Math.abs(mid - last) > 0.1) return false;
  const bid = Number(r.bid);
  const ask = Number(r.ask);
  if (bid > 0 && ask > 0 && ask >= bid) {
    if ((ask - bid) / mid > 0.3) return false;
  }
  return true;
}

function defaultFilters(): CallFilterState {
  return Layout.migrateFilterStateV1toV2(
    {
      quote: "all",
      dte: "all",
      earnings: "all",
      ticker: "",
      shares: "all",
      sectors: [],
      rubricBands: [],
      caps: [],
      risky: false,
      riskyPopRange: defaultCallsRiskyPopRange(),
    },
    "call",
  ) as CallFilterState;
}

function loadFilters(): CallFilterState {
  try {
    const raw = localStorage.getItem(CALL_FILTERS_KEY);
    if (!raw) return defaultFilters();
    const parsed = Layout.migrateFilterStateV1toV2(JSON.parse(raw), "call") as CallFilterState;
    if (parsed.shares !== "available") parsed.shares = "all";
    parsed.ticker = sanitizeTickerFilterValue(parsed.ticker).toUpperCase();
    parsed.risky = parsed.risky === true;
    parsed.riskyPopRange = parseCallsRiskyPopRange(
      parsed.riskyPopRange ?? (parsed as { riskyPopPcts?: unknown }).riskyPopPcts,
    );
    return parsed;
  } catch {
    return defaultFilters();
  }
}

function saveFilters(state: CallFilterState): void {
  localStorage.setItem(CALL_FILTERS_KEY, JSON.stringify(state));
}

function enrichRow(
  rec: WheelRecommendation,
  positions: PositionsSnapshot,
  stockAvgs: Map<string, number>,
): WheelRecommendation {
  const ticker = normalizeTicker(rec.underlying_ticker);
  const badges = rec.index_badges?.length ? rec.index_badges : indexBadgesForTicker(ticker);
  const stock = positions.stock.find((p) => normalizeTicker(p.ticker) === ticker);
  const openCalls = positions.options.filter(
    (p) => normalizeTicker(p.ticker) === ticker && /call/i.test(p.side) && !p.expired,
  );

  let openedCalls = rec.opened_calls;
  const avgCost = resolveShareAvgCostForTicker(ticker, stockAvgs, rec, stock);

  if (openedCalls == null) {
    openedCalls = openCalls.reduce((sum, p) => sum + (Number(p.contracts) || 0), 0);
  }

  const { held, available } = sharesForTicker(positions, ticker);

  return {
    ...rec,
    index_badges: badges,
    opened_calls: openedCalls,
    holding_avg_cost: avgCost,
    sfe_shares_held: held,
    sfe_shares_available: available,
  };
}

function callAdviceAttrs(row: WheelRecommendation) {
  return {
    underlying: row.underlying_ticker,
    side: row.side || "call",
    strike: row.strike,
    expiration: row.expiration_date,
    optionSymbol: row.option_symbol,
  };
}

function applyFilters(rows: WheelRecommendation[], state: CallFilterState): WheelRecommendation[] {
  let out = Layout.filterRecommendationRows(
    rows,
    state,
    "call",
    CALL_DTE_BUCKETS,
    isTradableQuote,
  ) as WheelRecommendation[];
  out = out.filter((row) => scopeAllowsRecommendation(row.underlying_ticker));
  if (state.shares === "available") {
    out = out.filter((r) => (r.sfe_shares_available ?? 0) > 0);
  }
  if (!state.risky) {
    out = out.filter((r) => !isRiskyCallRecommendation(r));
  } else {
    out = out.filter(
      (r) =>
        !isRiskyCallRecommendation(r) ||
        meetsRiskyPopRange(r.pop, state.riskyPopRange),
    );
  }
  return pinAdvisedScreenerRows(rows, out, "calls", callAdviceAttrs);
}

function stampCallEarnings(
  rows: WheelRecommendation[],
  extra: WheelRecommendation[] = [],
): WheelRecommendation[] {
  const snap = peekRecommendationsSnapshot();
  return applyTickerEarnings(rows, [...extra, ...(snap?.calls ?? []), ...(snap?.puts ?? [])]);
}

function mergeCallAdviceRows(
  filtered: WheelRecommendation[],
  cache: CallsPanelCache,
): WheelRecommendation[] {
  return appendSyntheticAdviceRows(
    filtered,
    cache.allRows,
    "calls",
    callAdviceAttrs,
    (seed) =>
      enrichAdviceScreenerRow(
        enrichRow(
          {
            ...adviceSeedToScreenerRow(seed),
            side: seed.side || "call",
          },
          cache.positions,
          cache.stockAvgs,
        ),
        cache.allRows,
        cache.positions,
      ),
  );
}

function activeDteBuckets(rows: WheelRecommendation[]): typeof CALL_DTE_BUCKETS {
  const keys = new Set<string>();
  for (const r of rows) {
    for (const b of CALL_DTE_BUCKETS) {
      const n = Number(r.dte);
      if (Number.isFinite(n) && n >= b.min && n <= b.max) keys.add(b.key);
    }
  }
  return CALL_DTE_BUCKETS.filter((b) => keys.has(b.key));
}

function visibleColumnIds(columns: ColumnState[], riskyMode = false): string[] {
  const lockedLast = "_actions";
  const ids = displayTableColumns(columns, isWebCompactMode()).map((column) => column.key);
  if (!ids.includes(lockedLast)) ids.push(lockedLast);
  else {
    const i = ids.indexOf(lockedLast);
    if (i >= 0 && i !== ids.length - 1) {
      ids.splice(i, 1);
      ids.push(lockedLast);
    }
  }
  if (!ids.includes("underlying_ticker")) ids.unshift("underlying_ticker");
  const filtered = ids.filter((id) => id !== "dte" && id !== "sfe_max_loss" && id !== "sfe_profit");
  if (riskyMode) {
    const avgAt = filtered.indexOf("holding_avg_cost");
    filtered.splice(
      avgAt >= 0 ? avgAt + 1 : filtered.length - 1,
      0,
      "sfe_max_loss",
      "sfe_profit",
    );
    if (!filtered.includes("next_earnings_date")) {
      const expAt = filtered.indexOf("expiration_date");
      filtered.splice(expAt >= 0 ? expAt + 1 : filtered.length - 1, 0, "next_earnings_date");
    }
  }
  return filtered;
}

function renderHeader(columns: ColumnState[], riskyMode = false): string {
  const metaMap = new Map(Layout.CALL_COLUMN_META.map((m) => [m.id, m]));
  const compact = isWebCompactMode();
  const ids = visibleColumnIds(columns, riskyMode);
  return ids
    .map((colId) => {
      const m = metaMap.get(colId) ?? EXTRA_COLUMN_META[colId];
      if (colId === "_actions") return '<th data-col="_actions"></th>';
      if (!m) return "";
      const clsParts: string[] = [];
      if (m.num) clsParts.push("num");
      if (m.heldTh) clsParts.push("held-th");
      if (colId === "underlying_ticker") {
        clsParts.push("recs-sticky-col", "sfe-ticker-col-header");
      }
      if (colId === "next_earnings_date") clsParts.push("sfe-earnings-header");
      if (colId === "sfe_profitability") clsParts.push("sfe-profit-header");
      if (colId === "broker_mark" || colId === "account_short") clsParts.push("sfe-mark-col");
      if (colId === "account_short") clsParts.push("sfe-acct-col");
      const cls = clsParts.length ? ` class="${clsParts.join(" ")}"` : "";
      const sortKey = "sortKey" in m ? m.sortKey : undefined;
      const sortType = "sortType" in m ? m.sortType : undefined;
      const sortAttr = sortKey ? ` data-sort="${sortKey}" data-type="${sortType ?? "str"}"` : "";
      const hint = Layout.columnHeaderHint(m, compact);
      const tipAttr = hint ? ` data-tip="${escAttr(hint)}"` : "";
      return `<th data-col="${colId}"${sortAttr}${tipAttr}${cls}><span class="th-inner"><span class="th-label">${esc(m.label)}</span></span></th>`;
    })
    .join("");
}


function sharesCell(rec: WheelRecommendation, colId: "held" | "open"): string {
  return renderScreenerSharesCellHtml(rec.sfe_shares_held, rec.sfe_shares_available, colId, esc);
}

function rubricCell(rec: WheelRecommendation): string {
  const tk = esc(rec.underlying_ticker ?? "");
  const st = rec.report_state;
  if (st === "missing") {
    return `<td class="rubric-cell" data-col="decision"><button type="button" class="muted wheel-rubric-missing" data-ticker="${tk}">No report</button></td>`;
  }
  if (st && st !== "available") {
    return `<td class="rubric-cell" data-col="decision"><span class="muted wheel-rubric-unknown">Band unavailable</span></td>`;
  }
  const decision = rec.decision?.trim() ?? "";
  if (!decision || !Layout.KNOWN_DECISIONS.has(decision)) {
    const label = decision ? "Unmapped band" : "Band unavailable";
    const cls = decision ? "wheel-rubric-unmapped" : "wheel-rubric-unavailable";
    return `<td class="rubric-cell" data-col="decision"><button type="button" class="wheel-rubric-link muted ${cls}" data-ticker="${tk}">${esc(label)}</button></td>`;
  }
  const disp = Layout.displayDecision(decision);
  const tier = Layout.verdictTier(decision);
  return `<td class="rubric-cell" data-col="decision"><button type="button" class="wheel-rubric-link wheel-rubric-${tier}" data-ticker="${tk}">${esc(disp)}</button></td>`;
}

function expiryCell(rec: WheelRecommendation, compact: boolean): string {
  const inner = renderScreenerExpiryInnerHtml(rec, esc);
  const cls = compact ? "sfe-web-compact-cell" : "sfe-screener-expiry-cell";
  const tip = screenerExpiryEarningsTip(rec);
  const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
  const expDate = screenerExpiryDateLabel(rec.expiration_date);
  const dateAttr = expDate !== "—" ? ` data-expiration-date="${escAttr(expDate)}"` : "";
  return `<td class="${cls}" data-col="expiration_date"${dateAttr}${tipAttr}>${inner}</td>`;
}

function targetCell(rec: WheelRecommendation): string {
  return `<td class="num" data-col="target_price">${fmtUsd(rec.target_price)}</td>`;
}

function strikeCell(rec: WheelRecommendation): string {
  const vs = callStrikeVsAvgCost(rec.strike, rec.holding_avg_cost);
  const cls = callStrikeVsAvgValueClass(vs);
  const tip = callStrikeVsAvgTip(rec.strike, rec.holding_avg_cost);
  const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
  return `<td class="num ${cls}" data-col="strike"${tipAttr}>${fmtNum(rec.strike, 2)}</td>`;
}

function roiCell(roi: number | undefined): string {
  const v = Number(roi);
  const cls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "";
  return `<td class="num ${cls}" data-col="roi">${fmtWheelRatio(roi)}</td>`;
}

function earningsCell(rec: WheelRecommendation): string {
  const date = fmtEarningsDate(rec.next_earnings_date);
  const tip = earningsDateTip(rec);
  const within = earningsWithinExpiry(rec);
  const withinAttr = within ? ' data-earnings-within-expiry="true"' : "";
  if (date === "—") {
    return `<td class="sfe-earnings-cell sfe-web-compact-cell" data-col="next_earnings_date" data-tip="${escAttr(tip)}"${withinAttr}>—</td>`;
  }
  const inDays = fmtEarningsInDays(rec.next_earnings_date);
  const subCls = within ? "sfe-earnings-within-expiry" : "";
  const inner = `${esc(date)}${compactSubline(inDays, subCls)}`;
  return `<td class="sfe-earnings-cell sfe-web-compact-cell" data-col="next_earnings_date" data-tip="${escAttr(tip)}"${withinAttr}>${inner}</td>`;
}

function profitCell(rec: WheelRecommendation): string {
  const result = recommendationProfitability(rec);
  return `<td class="num sfe-profit-cell" data-col="sfe_profitability" data-tip="${escAttr(result.tip)}">${fmtProfitPct(result.pct)}</td>`;
}

function underlyingCell(rec: WheelRecommendation, idx: number): string {
  const ticker = rec.underlying_ticker ?? "—";
  const badges = renderIndexBadgesHtml(rec.index_badges);
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "call",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "calls",
    );
  const adviceBadge = advised ? adviceBadgeHtml() : "";
  const tickerHtml = `<span class="recs-ticker${rec.is_golden ? " is-golden" : ""}" data-ticker="${escAttr(ticker)}" data-side="call" data-row-idx="${idx}" title="Open ${escAttr(ticker)} on Stock Forge">${esc(ticker)}<span class="tk-ic">↗</span></span>`;
  const compactCls = isWebCompactMode() ? " sfe-web-compact-cell" : "";
  return `<td class="tk-cell${compactCls}" data-col="underlying_ticker"><div class="tk-row">${tickerHtml}${badges}${adviceBadge}</div></td>`;
}

function underlyingPriceCell(rec: WheelRecommendation, compact: boolean): string {
  if (compact) {
    return `<td class="num sfe-web-compact-cell" data-col="underlying_price">${fmtUsd(rec.underlying_price)}${compactSubline(`tgt ${fmtUsd(rec.target_price)}`, "sfe-web-compact-sub")}</td>`;
  }
  return `<td class="num" data-col="underlying_price">${fmtUsd(rec.underlying_price)}</td>`;
}

function renderCell(colId: string, rec: WheelRecommendation, idx: number): string {
  if (colId === "broker_mark") return brokerMarkCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  if (colId === "account_short") return accountShortCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  const compact = isWebCompactMode();
  switch (colId) {
    case "underlying_ticker":
      return underlyingCell(rec, idx);
    case "holding_avg_cost": {
      const avail = rec.sfe_shares_available;
      const availLabel =
        avail != null && Number.isFinite(avail) ? `avail ${fmtNum(avail, 0)}` : "—";
      const availCls =
        avail != null && avail > 0 ? "roi-strong" : "sfe-web-compact-sub";
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="holding_avg_cost">${fmtUsd(rec.holding_avg_cost)}${compactSubline(availLabel, availCls)}</td>`;
      }
      return `<td class="num" data-col="holding_avg_cost">${fmtUsd(rec.holding_avg_cost)}</td>`;
    }
    case "market_cap":
      return `<td class="num" data-col="market_cap">${Layout.fmtMktCapBillions(rec.market_cap)}</td>`;
    case "sector": {
      const s = rec.sector?.trim();
      return s
        ? `<td class="sector-cell" data-col="sector" title="${escAttr(s)}">${esc(s)}</td>`
        : `<td class="sector-cell muted" data-col="sector">—</td>`;
    }
    case "strike":
      if (compact) {
        const vs = callStrikeVsAvgCost(rec.strike, rec.holding_avg_cost);
        const cls = callStrikeVsAvgValueClass(vs);
        const tip = callStrikeVsAvgTip(rec.strike, rec.holding_avg_cost);
        const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
        return `<td class="num sfe-web-compact-cell ${cls}" data-col="strike"${tipAttr}>${fmtNum(rec.strike, 2)}${compactSubline(`POP: ${fmtWheelRatio(rec.pop)}`, "sfe-web-compact-sub")}</td>`;
      }
      return strikeCell(rec);
    case "underlying_price":
      return underlyingPriceCell(rec, compact);
    case "target_price":
      return targetCell(rec);
    case "decision":
      return rubricCell(rec);
    case "expiration_date":
      return expiryCell(rec, compact);
    case "next_earnings_date":
      return earningsCell(rec);
    case "dte":
      return `<td class="num" data-col="dte">${rec.dte != null ? rec.dte : "—"}</td>`;
    case "mid": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="mid">${fmtUsd(rec.mid)}${compactSubline(`last ${fmtUsd(rec.last_price)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="mid">${fmtUsd(rec.mid)}</td>`;
    }
    case "last_price":
      return `<td class="num" data-col="last_price">${fmtUsd(rec.last_price)}</td>`;
    case "available_call_contracts":
      return `<td class="num" data-col="available_call_contracts">${rec.available_call_contracts != null ? rec.available_call_contracts : "—"}</td>`;
    case "collateral":
      return `<td class="num" data-col="collateral">${fmtUsd(rec.collateral)}</td>`;
    case "roi":
      return roiCell(rec.roi);
    case "pop":
      return `<td class="num" data-col="pop">${fmtWheelRatio(rec.pop)}</td>`;
    case "sfe_max_loss": {
      const shares = riskyCoveredShares(rec);
      const loss = callAssignmentMaxLoss(rec.holding_avg_cost, rec.strike, shares);
      const text = loss == null ? "—" : loss > 0 ? fmtUsd(loss) : "$0";
      const cls = loss != null && loss > 0 ? "sfe-neg" : "";
      return `<td class="num ${cls}" data-col="sfe_max_loss" data-tip="${escAttr(`Locked-in share loss if assigned with all ${fmtNum(shares)} held shares covered: (avg cost − strike) × shares.`)}">${text}</td>`;
    }
    case "sfe_profit": {
      const shares = riskyCoveredShares(rec);
      const profit = callCoveredPremiumProfit(rec, shares);
      const text = profit == null ? "—" : fmtUsd(profit);
      const cls = profit != null && profit > 0 ? "roi-strong" : "";
      return `<td class="num ${cls}" data-col="sfe_profit" data-tip="${escAttr(`Premium if you sell covered calls on all ${fmtNum(shares)} held shares: last (or mid) × shares.`)}">${text}</td>`;
    }
    case "sfe_profitability": {
      if (compact) {
        const result = recommendationProfitability(rec);
        const v = Number(rec.roi);
        const roiCls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "sfe-web-compact-sub";
        return `<td class="num sfe-web-compact-cell sfe-profit-cell" data-col="sfe_profitability" data-tip="${escAttr(result.tip)}">${fmtProfitPct(result.pct)}${compactSubline(fmtWheelRatio(rec.roi), roiCls)}</td>`;
      }
      return profitCell(rec);
    }
    case "open":
      return sharesCell(rec, "open");
    case "_actions":
      return `<td class="pos-actions" data-col="_actions">${renderFocusRecRowMenu({ ...rec, side: rec.side || "call" })}</td>`;
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

function callContractKey(rec: WheelRecommendation): string {
  return screenerContractKey({
    underlying: rec.underlying_ticker,
    side: rec.side || "call",
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  });
}

function renderCallRow(
  rec: WheelRecommendation,
  index: number,
  columns: ColumnState[],
  riskyMode = false,
): string {
  const ids = visibleColumnIds(columns, riskyMode);
  const cells = ids.map((id) => renderCell(id, rec, index)).join("");
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "call",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "calls",
    );
  const rowClass = rec.sfe_advice ? "sfe-row-ai-advice" : advised ? "sfe-row-advised-line" : "";
  const key = callContractKey(rec);
  return `<tr class="${rowClass}" ${adviceRowAttrs({
    underlying: rec.underlying_ticker,
    side: rec.side || "call",
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  })} data-row-key="${escAttr(key)}" data-row-idx="${index}"${rec.sfe_advice ? ' data-sfe-advice-synthetic="1"' : ""}>${cells}</tr>`;
}

function renderRows(
  rows: WheelRecommendation[],
  columns: ColumnState[],
  riskyMode = false,
): string {
  const ids = visibleColumnIds(columns, riskyMode);
  if (!rows.length) {
    return `<tr><td colspan="${Math.max(ids.length, 1)}" class="muted">No calls match your filters.</td></tr>`;
  }
  return rows.map((rec, i) => renderCallRow(rec, i, columns, riskyMode)).join("");
}

function renderFilterBar(state: CallFilterState, allRows: WheelRecommendation[]): string {
  const dteBuckets = activeDteBuckets(allRows);
  const dteChips = [
    `<button type="button" class="year-chip${state.dte === "all" ? " active" : ""}" data-call-dte="all">All</button>`,
    ...dteBuckets.map(
      (b) =>
        `<button type="button" class="year-chip${state.dte === b.key ? " active" : ""}" data-call-dte="${escAttr(b.key)}">${esc(b.label)}</button>`,
    ),
  ].join("");

  return `
    <div class="filter-bar recs-filter-bar" data-tour-anchor="wheel-calls-filters">
      <div class="filter-field">
        <label for="wheelCallTicker">Ticker<span class="hint" tabindex="0" role="img" data-tip="Prefix match — case-insensitive.">i</span></label>
        <input type="text" id="wheelCallTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label>Quote<span class="hint" tabindex="0" role="img" data-tip="Tradable = recent two-sided quote; All also shows stale / wide-spread contracts.">i</span></label>
        <div class="year-chips" id="wheelCallQuoteFilter">
          <button type="button" class="year-chip${state.quote === "tradable" ? " active" : ""}" data-call-quote="tradable">Tradable</button>
          <button type="button" class="year-chip${state.quote === "all" ? " active" : ""}" data-call-quote="all">All</button>
        </div>
      </div>
      <div class="filter-field">
        <label>DTE<span class="hint" tabindex="0" role="img" data-tip="Filter screened contracts by days-to-expiration bucket.">i</span></label>
        <div class="year-chips" id="wheelCallDteFilter">${dteChips}</div>
      </div>
      <div class="filter-field">
        <label>Shares<span class="hint" tabindex="0" role="img" data-tip="Available shows tickers where you still have shares not tied to open short call contracts (avail &gt; 0).">i</span></label>
        <div class="year-chips" id="wheelCallSharesFilter" role="group">
          <button type="button" class="year-chip${state.shares === "all" ? " active" : ""}" data-call-shares="all" aria-pressed="${state.shares === "all" ? "true" : "false"}">All</button>
          <button type="button" class="year-chip${state.shares === "available" ? " active" : ""}" data-call-shares="available" aria-pressed="${state.shares === "available" ? "true" : "false"}">Available</button>
        </div>
      </div>
      ${advisoryFilterFieldHtml()}
      <div class="filter-field">
        <label>Risky<span class="hint" tabindex="0" role="img" data-tip="Include covered-call strikes below your average share cost. When on, set a min/max PoP range. Max loss and Profit size to all held shares as if every lot is covered.">i</span></label>
        <div class="year-chips" id="wheelCallRiskyFilter" role="group">
          <button type="button" class="year-chip${state.risky ? " active" : ""}" data-call-risky-toggle aria-pressed="${state.risky ? "true" : "false"}">Risky</button>
        </div>
      </div>
      ${
        state.risky
          ? `<div class="filter-field sfe-calls-risky-pct-field">
        <label for="wheelCallRiskyPopMin">PoP<span class="hint" tabindex="0" role="img" data-tip="Show below-avg suggestions whose PoP falls between the two handles. Full 75–100% includes every below-avg contract, including PoP under 75.">i</span></label>
        ${renderCallsRiskyPopRangeSlider(state.riskyPopRange, { id: "wheelCallRiskyPop" })}
      </div>`
          : ""
      }
      <div class="filter-field">
        <div class="sfe-filter-reset-actions sfe-web-calls-columns-host">
          <button type="button" class="small sfe-reset-filters" id="wheelCallResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          ${screenerAdviceButtonHtml()}
        </div>
      </div>
    </div>`;
}

function callsFilterNoteText(count: number, stale = false): string {
  const staleNote = stale ? " · showing cached data, refreshing…" : "";
  return `${count} contract${count === 1 ? "" : "s"}${staleNote}`;
}

function existingRowKey(tr: HTMLTableRowElement): string {
  const stored = (tr.getAttribute("data-row-key") || "").trim();
  if (stored) return stored;
  return screenerContractKey({
    underlying: tr.getAttribute("data-underlying"),
    side: tr.getAttribute("data-side") || "call",
    strike: Number(tr.getAttribute("data-strike")),
    expiration: tr.getAttribute("data-exp"),
    optionSymbol: tr.getAttribute("data-option-symbol"),
  });
}

function panelHasDisplayedContent(panel: HTMLElement): boolean {
  return panel.querySelector(".sfe-web-native-calls") != null;
}

function patchCallRow(
  tr: HTMLTableRowElement,
  rec: WheelRecommendation,
  index: number,
  riskyMode = false,
): void {
  const columns = resolveWebTableColumns(TABLE_ID);
  const key = callContractKey(rec);
  if (key) tr.setAttribute("data-row-key", key);
  for (const colId of visibleColumnIds(columns, riskyMode)) {
    if (colId === "_actions" || colId === "underlying_ticker") continue;
    const td = tr.querySelector<HTMLTableCellElement>(`td[data-col="${colId}"]`);
    if (!td) continue;
    morphTableCell(td, renderCell(colId, rec, index));
  }
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "call",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "calls",
    );
  tr.classList.toggle("sfe-row-ai-advice", Boolean(rec.sfe_advice));
  tr.classList.toggle("sfe-row-advised-line", Boolean(advised && !rec.sfe_advice));
  tr.setAttribute("data-row-idx", String(index));
}

function patchCallsTableBody(
  tbody: HTMLElement,
  rows: WheelRecommendation[],
  columns: ColumnState[],
  riskyMode = false,
): void {
  reconcileTableBodyByKey(tbody, rows, {
    rowSelector: "tr[data-option-symbol], tr[data-underlying], tr[data-row-key]",
    rowKey: callContractKey,
    existingKey: existingRowKey,
    emptyHtml: `<tr><td colspan="${Math.max(visibleColumnIds(columns, riskyMode).length, 1)}" class="muted">No calls match your filters.</td></tr>`,
    createRow: (rec, index) => rowFromHtml(renderCallRow(rec, index, columns, riskyMode)),
    patchRow: (tr, rec, index) => patchCallRow(tr, rec, index, riskyMode),
  });
}

function currentCallsRows(cache: CallsPanelCache, state: CallFilterState): WheelRecommendation[] {
  return mergeCallAdviceRows(applyFilters(cache.allRows, state), cache);
}

function softUpdateCallsPanel(
  panel: HTMLElement,
  state: CallFilterState,
  cache: CallsPanelCache,
): void {
  callsPanelCache = cache;
  const columns = resolveWebTableColumns(TABLE_ID);
  const filtered = currentCallsRows(cache, state);
  const tbody = panel.querySelector<HTMLElement>("#wheelCallsBody");
  if (tbody) patchCallsTableBody(tbody, filtered, columns, state.risky);
  const note = panel.querySelector<HTMLElement>("#wheelCallFilterNote");
  if (note) note.textContent = callsFilterNoteText(filtered.length);
  applyAdviceMarks(panel, "calls");
}

function renderPanelShell(
  state: CallFilterState,
  allRows: WheelRecommendation[],
  filtered: WheelRecommendation[],
  columns: ColumnState[],
  stale = false,
): string {
  const head = renderHeader(columns, state.risky);
  const body = renderRows(filtered, columns, state.risky);
  return `
    <div data-wheel-view="calls" class="panel sfe-web-native-calls" style="margin-top:12px;">
      <h2 data-tour-anchor="wheel-calls-heading">Covered-call contract screener<span class="hint" tabindex="0" role="img" data-tip="Covered call contracts from shares you hold that pass your screen settings. For independent review only — not a recommendation.">i</span></h2>
      ${renderFilterBar(state, allRows)}
      <p class="muted small recs-filter-note" id="wheelCallFilterNote">${callsFilterNoteText(filtered.length, stale)}</p>
      <div class="recs-wrap" data-tour-anchor="wheel-calls-table">
        <div class="recs-hscroll" id="wheelCallsHScroll">
          <table class="recs recs-compact recs-sticky-col wheel-screener-calls" id="wheelCallsTable"${renderTablePinnedAttr(columns)}>
            <thead><tr id="wheelCallsHeadRow">${head}</tr></thead>
            <tbody id="wheelCallsBody">${body}</tbody>
          </table>
        </div>
      </div>
    </div>`;
}

function enhanceCallFilterSelects(root: HTMLElement): void {
  const api = (window as Window & { UnifiedMultiSelect?: { enhanceAll: (scope?: ParentNode) => void } })
    .UnifiedMultiSelect;
  api?.enhanceAll(root);
}

function bindFilters(
  root: HTMLElement,
  state: CallFilterState,
  rerender: () => void,
  onRiskyChanged?: () => void,
): void {
  root.querySelector("#wheelCallQuoteFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-call-quote]");
    if (!btn) return;
    state.quote = btn.dataset.callQuote === "all" ? "all" : "tradable";
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelCallDteFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-call-dte]");
    if (!btn) return;
    state.dte = btn.dataset.callDte ?? "all";
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelCallSharesFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-call-shares]");
    if (!btn) return;
    state.shares = btn.dataset.callShares === "available" ? "available" : "all";
    saveFilters(state);
    rerender();
  });

  const ticker = root.querySelector<HTMLInputElement>("#wheelCallTicker");
  ticker?.addEventListener("input", () => {
    state.ticker = sanitizeTickerFilterValue(ticker.value).toUpperCase();
    saveFilters(state);
    rerender();
  });

  guardTickerFilterInputs(root);

  root.querySelector("[data-call-risky-toggle]")?.addEventListener("click", () => {
    state.risky = !state.risky;
    saveFilters(state);
    if (state.risky && onRiskyChanged) onRiskyChanged();
    else rerender();
  });

  bindCallsRiskyPopRangeSlider(root, (range) => {
    state.riskyPopRange = range;
    saveFilters(state);
    const live = callsPanelCache;
    if (live) softUpdateCallsPanel(root, state, live);
    else rerender();
  });

  root.querySelector("#wheelCallResetFilters")?.addEventListener("click", () => {
    Object.assign(state, defaultFilters());
    saveFilters(state);
    rerender();
  });
}

async function mergeRiskyCallRows(
  cache: CallsPanelCache,
  force: boolean,
): Promise<CallsPanelCache> {
  if (cache.riskyMerged && !force) return cache;
  const risky = await fetchRiskyCallRecommendations(force);
  const allRows = stampCallEarnings(
    mergeCallRecommendations(cache.allRows, risky).map((r) =>
      enrichRow(r, cache.positions, cache.stockAvgs),
    ),
  );
  return {
    ...cache,
    allRows,
    riskyMerged: true,
    fetchedAt: Date.now(),
  };
}

function buildCallsPanelCacheFromPeek(): CallsPanelCache | null {
  const recSnap = peekRecommendationsSnapshot();
  const positions = peekPositionsSnapshot();
  if (!recSnap || !positions) return null;
  return {
    allRows: stampCallEarnings(recSnap.calls.map((r) => enrichRow(r, positions, new Map()))),
    positions,
    stockAvgs: new Map(),
    fetchedAt: Math.max(recSnap.fetchedAt, positions.fetchedAt),
    riskyMerged: false,
  };
}

function mountCallsPanel(
  panel: HTMLElement,
  state: CallFilterState,
  cache: CallsPanelCache,
  stale = false,
): () => void {
  callsPanelCache = cache;
  let showStaleBanner = stale;

  const loadRiskyIfNeeded = (): void => {
    const live = callsPanelCache ?? cache;
    if (live.riskyMerged) {
      rerender();
      return;
    }
    const note = panel.querySelector<HTMLElement>("#wheelCallFilterNote");
    if (note) note.textContent = "Loading risky calls…";
    void mergeRiskyCallRows(live, false)
      .then((next) => {
        if (callsMountedPanel !== panel) return;
        callsPanelCache = next;
        rerender();
      })
      .catch(() => {
        if (callsMountedPanel === panel) rerender();
      });
  };

  const rerender = (): void => {
    withPreservedInputFocus(() => {
      const live = callsPanelCache ?? cache;
      teardownMultiSelectPortals(panel);
      const columns = resolveWebTableColumns(TABLE_ID);
      const filtered = mergeCallAdviceRows(applyFilters(live.allRows, state), live);
      panel.innerHTML = renderPanelShell(state, live.allRows, filtered, columns, showStaleBanner);
      showStaleBanner = false;
      bindFilters(panel, state, rerender, loadRiskyIfNeeded);
      enhanceCallFilterSelects(panel);
      bindScreenerAdvice(panel, "calls");
      const resetBtn = panel.querySelector<HTMLElement>("#wheelCallResetFilters");
      if (resetBtn) {
        ensureColumnPickerBesideReset(resetBtn, {
          tableId: TABLE_ID,
          title: "Configure Calls columns",
          defaults: getTableColumnDefaults(TABLE_ID),
          onApply: () => rerender(),
        });
      }
      void hydrateAdviceScreenerRows(filtered).then((changed) => {
        if (changed && callsMountedPanel === panel && callsMountedPanel.getAttribute("data-wheel-view") === "calls") {
          rerender();
        }
      });
    });
  };

  callsRerender = rerender;
  rerender();
  return rerender;
}

async function loadCallsPanelCache(force: boolean): Promise<CallsPanelCache> {
  const cacheFresh =
    callsPanelCache &&
    Date.now() - callsPanelCache.fetchedAt < CALLS_CACHE_MS &&
    !callsCacheMissingAvgs(callsPanelCache);
  if (!force && cacheFresh) {
    return callsPanelCache!;
  }

  if (callsPanelInflight && !force) {
    return callsPanelInflight;
  }

  const load = async (): Promise<CallsPanelCache> => {
    try {
      const [recSnap, positions, stockAvgs] = await Promise.all([
        loadRecommendationsSnapshot(force),
        loadPositionsSnapshot(force),
        loadOwnLedgerStrikeAvgMap(force),
        ensureLedgerMarkIndex(force).catch(() => null),
      ]);
      let allRows = stampCallEarnings(
        recSnap.calls.map((r) => enrichRow(r, positions, stockAvgs)),
        [...recSnap.calls, ...recSnap.puts],
      );
      const wantRisky = loadFilters().risky;
      let riskyMerged = false;
      if (wantRisky) {
        const risky = await fetchRiskyCallRecommendations(force);
        allRows = stampCallEarnings(
          mergeCallRecommendations(allRows, risky).map((r) =>
            enrichRow(r, positions, stockAvgs),
          ),
          [...recSnap.calls, ...recSnap.puts],
        );
        riskyMerged = true;
      }
      const cache: CallsPanelCache = {
        allRows,
        positions,
        stockAvgs,
        fetchedAt: Date.now(),
        riskyMerged,
      };
      callsPanelCache = cache;
      return cache;
    } catch (err) {
      if (callsPanelCache) return callsPanelCache;
      throw err;
    } finally {
      callsPanelInflight = null;
    }
  };

  callsPanelInflight = load();
  return callsPanelInflight;
}

export async function renderNativeCallsPanel(
  panel: HTMLElement,
  opts: { force?: boolean } = {},
): Promise<void> {
  const force = opts.force ?? false;
  const state = loadFilters();
  callsMountedPanel = panel;

  if (!callsPanelCache) {
    const peek = buildCallsPanelCacheFromPeek();
    if (peek) callsPanelCache = peek;
  }

  const hasCached = Boolean(callsPanelCache);
  const displayed = panelHasDisplayedContent(panel);
  if (!hasCached && !displayed) {
    panel.innerHTML = `<div class="sfe-web-panel-loading">Loading call recommendations…</div>`;
  }

  try {
    if (hasCached && !force) {
      if (!displayed) mountCallsPanel(panel, state, callsPanelCache!);
      const needsAvgs = callsCacheMissingAvgs(callsPanelCache);
      const stale = Date.now() - callsPanelCache!.fetchedAt >= CALLS_CACHE_MS;
      if (needsAvgs || stale) {
        void loadCallsPanelCache(needsAvgs).then((cache) => {
          if (callsMountedPanel !== panel) return;
          if (panelHasDisplayedContent(panel)) softUpdateCallsPanel(panel, state, cache);
          else mountCallsPanel(panel, state, cache);
        });
      }
      return;
    }

    if (hasCached && displayed) {
      try {
        const cache = await loadCallsPanelCache(force);
        if (callsMountedPanel !== panel) return;
        softUpdateCallsPanel(panel, state, cache);
        return;
      } catch {
        return;
      }
    }

    if (hasCached) {
      mountCallsPanel(panel, state, callsPanelCache!, true);
    }

    const cache = await loadCallsPanelCache(force);
    if (callsMountedPanel !== panel) return;
    mountCallsPanel(panel, state, cache);
  } catch (err) {
    if (callsMountedPanel !== panel) return;
    paintPanelLoadError(panel, err, {
      title: "Covered-call contract screener",
      view: "calls",
      fallback: "Could not load call recommendations.",
    });
  }
}

export async function refreshNativeCallsPanelInBackground(panel: HTMLElement): Promise<void> {
  callsMountedPanel = panel;
  invalidateWheelSnapshots();
  const state = loadFilters();
  if (callsPanelCache && panelHasDisplayedContent(panel)) {
    try {
      const cache = await loadCallsPanelCache(true);
      if (callsMountedPanel === panel) softUpdateCallsPanel(panel, state, cache);
    } catch {
      /* keep showing current rows */
    }
    return;
  }
  await renderNativeCallsPanel(panel, { force: true });
}

export function invalidateCallsPanelCache(): void {
  callsPanelCache = null;
  callsPanelInflight = null;
  invalidateWheelSnapshots();
}
